import { BoxedIconProps } from '../../../shared/boxed-icon';

import AddToDrive from '@mui/icons-material/AddToDrive';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import PermContactCalendarOutlined from '@mui/icons-material/PermContactCalendarOutlined';

export interface TriggerEventParameter {
  value: string;
  displayValue: string;
  displayInfoValue: string;
  valueType: string;
}

export interface TriggerEventOption {
  value: string;
  displayValue: string;
  displayInfoValue: string;
  type: string;
  lowestEntityAllowed: string;
  group?: string;
  availableParameters: TriggerEventParameter[];
  tags?: string[];
}

export const triggerEventOptions: TriggerEventOption[] = [
  // https://developers.google.com/admin-sdk/reports/v1/appendix/activity/drive#create
  {
    value: 'create',
    displayValue: 'Document Created',
    displayInfoValue: 'Documents are created.',
    type: 'drive',
    lowestEntityAllowed: 'User',
    tags: ['New'],
    availableParameters: [
      {
        value: 'actor_is_collaborator_account',
        displayValue: 'Actor is Collaborator Account',
        displayInfoValue: 'Whether the actor is a collaborator account.',
        valueType: 'boolean',
      },
      {
        value: 'billable',
        displayValue: 'Billable',
        displayInfoValue: 'Whether this activity is billable.',
        valueType: 'boolean',
      },
      {
        value: 'doc_id',
        displayValue: 'Document ID',
        displayInfoValue: 'Document ID.',
        valueType: 'string',
      },
      {
        value: 'doc_title',
        displayValue: 'Document Title',
        displayInfoValue: 'Document title.',
        valueType: 'string',
      },
      {
        value: 'doc_type',
        displayValue: 'Document Type',
        displayInfoValue:
          'Document type. Possible values: document, drawing, folder, form, html, jam, jpeg, mp4, mpeg, msexcel, mspowerpoint, msword, pdf, png, presentation, quicktime, script, shortcut, sites, spreadsheet, shared_drive, txt, unknown',
        valueType: 'string',
      },
      {
        value: 'is_encrypted',
        displayValue: 'Is Encrypted',
        displayInfoValue: 'Whether the file is client-side encrypted.',
        valueType: 'boolean',
      },
      {
        value: 'originating_app_id',
        displayValue: 'Originating App ID',
        displayInfoValue:
          'The Google Cloud Project ID of the application that performed the action.',
        valueType: 'string',
      },
      {
        value: 'owner',
        displayValue: 'Owner',
        displayInfoValue: `The email address of the document's owner. For items contained in a shared drive, the owner is the name of the shared drive.`,
        valueType: 'string',
      },
      {
        value: 'owner_is_shared_drive',
        displayValue: 'Owner is Shared Drive',
        displayInfoValue: `Boolean flag denoting whether the owner is a shared drive.`,
        valueType: 'boolean',
      },
      {
        value: 'owner_shared_drive_id',
        displayValue: 'Owner Shared Drive ID',
        displayInfoValue: `The shared drive root ID if the document owner is a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'primary_event',
        displayValue: 'Primary Event',
        displayInfoValue: `Whether this event is a primary event. A single user action in Drive can generate several events, multiple of which can be primary (primary_event=true).`,
        valueType: 'boolean',
      },
      {
        value: 'shared_drive_id',
        displayValue: 'Shared Drive ID',
        displayInfoValue: `The unique identifier of the shared drive. Only populated for events or items from a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'visibility',
        displayValue: 'Visibility',
        displayInfoValue: `Visibility of target file. Possible values: people_with_link, people_within_domain_with_link, private, public_in_the_domain, public_on_the_web, shared_externally, shared_internally, unknown`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'delete',
    displayValue: 'Documents Deleted',
    displayInfoValue: 'Documents are deleted.',
    type: 'drive',
    lowestEntityAllowed: 'User',
    availableParameters: [
      {
        value: 'actor_is_collaborator_account',
        displayValue: 'Actor is Collaborator Account',
        displayInfoValue: 'Whether the actor is a collaborator account.',
        valueType: 'boolean',
      },
      {
        value: 'billable',
        displayValue: 'Billable',
        displayInfoValue: 'Whether this activity is billable.',
        valueType: 'boolean',
      },
      {
        value: 'doc_id',
        displayValue: 'Document ID',
        displayInfoValue: 'Document ID.',
        valueType: 'string',
      },
      {
        value: 'doc_title',
        displayValue: 'Document Title',
        displayInfoValue: 'Document title.',
        valueType: 'string',
      },
      {
        value: 'doc_type',
        displayValue: 'Document Type',
        displayInfoValue:
          'Document type. Possible values: document, drawing, folder, form, html, jam, jpeg, mp4, mpeg, msexcel, mspowerpoint, msword, pdf, png, presentation, quicktime, script, shortcut, sites, spreadsheet, shared_drive, txt, unknown',
        valueType: 'string',
      },
      {
        value: 'is_encrypted',
        displayValue: 'Is Encrypted',
        displayInfoValue: 'Whether the file is client-side encrypted.',
        valueType: 'boolean',
      },
      {
        value: 'originating_app_id',
        displayValue: 'Originating App ID',
        displayInfoValue:
          'The Google Cloud Project ID of the application that performed the action.',
        valueType: 'string',
      },
      {
        value: 'owner',
        displayValue: 'Owner',
        displayInfoValue: `The email address of the document's owner. For items contained in a shared drive, the owner is the name of the shared drive.`,
        valueType: 'string',
      },
      {
        value: 'owner_is_shared_drive',
        displayValue: 'Owner is Shared Drive',
        displayInfoValue: `Boolean flag denoting whether the owner is a shared drive.`,
        valueType: 'boolean',
      },
      {
        value: 'owner_shared_drive_id',
        displayValue: 'Owner Shared Drive ID',
        displayInfoValue: `The shared drive root ID if the document owner is a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'primary_event',
        displayValue: 'Primary Event',
        displayInfoValue: `Whether this event is a primary event. A single user action in Drive can generate several events, multiple of which can be primary (primary_event=true).`,
        valueType: 'boolean',
      },
      {
        value: 'shared_drive_id',
        displayValue: 'Shared Drive ID',
        displayInfoValue: `The unique identifier of the shared drive. Only populated for events or items from a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'visibility',
        displayValue: 'Visibility',
        displayInfoValue: `Visibility of target file. Possible values: people_with_link, people_within_domain_with_link, private, public_in_the_domain, public_on_the_web, shared_externally, shared_internally, unknown`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'download',
    displayValue: 'Documents Downloaded',
    displayInfoValue: 'Documents are downloaded.',
    type: 'drive',
    lowestEntityAllowed: 'User',
    availableParameters: [
      {
        value: 'actor_is_collaborator_account',
        displayValue: 'Actor is Collaborator Account',
        displayInfoValue: 'Whether the actor is a collaborator account.',
        valueType: 'boolean',
      },
      {
        value: 'billable',
        displayValue: 'Billable',
        displayInfoValue: 'Whether this activity is billable.',
        valueType: 'boolean',
      },
      {
        value: 'doc_id',
        displayValue: 'Document ID',
        displayInfoValue: 'Document ID.',
        valueType: 'string',
      },
      {
        value: 'doc_title',
        displayValue: 'Document Title',
        displayInfoValue: 'Document title.',
        valueType: 'string',
      },
      {
        value: 'doc_type',
        displayValue: 'Document Type',
        displayInfoValue:
          'Document type. Possible values: document, drawing, folder, form, html, jam, jpeg, mp4, mpeg, msexcel, mspowerpoint, msword, pdf, png, presentation, quicktime, script, shortcut, sites, spreadsheet, shared_drive, txt, unknown',
        valueType: 'string',
      },
      {
        value: 'is_encrypted',
        displayValue: 'Is Encrypted',
        displayInfoValue: 'Whether the file is client-side encrypted.',
        valueType: 'boolean',
      },
      {
        value: 'originating_app_id',
        displayValue: 'Originating App ID',
        displayInfoValue:
          'The Google Cloud Project ID of the application that performed the action.',
        valueType: 'string',
      },
      {
        value: 'owner',
        displayValue: 'Owner',
        displayInfoValue: `The email address of the document's owner. For items contained in a shared drive, the owner is the name of the shared drive.`,
        valueType: 'string',
      },
      {
        value: 'owner_is_shared_drive',
        displayValue: 'Owner is Shared Drive',
        displayInfoValue: `Boolean flag denoting whether the owner is a shared drive.`,
        valueType: 'boolean',
      },
      {
        value: 'owner_shared_drive_id',
        displayValue: 'Owner Shared Drive ID',
        displayInfoValue: `The shared drive root ID if the document owner is a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'primary_event',
        displayValue: 'Primary Event',
        displayInfoValue: `Whether this event is a primary event. A single user action in Drive can generate several events, multiple of which can be primary (primary_event=true).`,
        valueType: 'boolean',
      },
      {
        value: 'shared_drive_id',
        displayValue: 'Shared Drive ID',
        displayInfoValue: `The unique identifier of the shared drive. Only populated for events or items from a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'visibility',
        displayValue: 'Visibility',
        displayInfoValue: `Visibility of target file. Possible values: people_with_link, people_within_domain_with_link, private, public_in_the_domain, public_on_the_web, shared_externally, shared_internally, unknown`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'change_user_access',
    displayValue: 'Documents Shared',
    displayInfoValue: 'Documents are shared.',
    type: 'drive',
    lowestEntityAllowed: 'User',
    tags: ['New'],
    availableParameters: [
      {
        value: 'actor_is_collaborator_account',
        displayValue: 'Actor is Collaborator Account',
        displayInfoValue: 'Whether the actor is a collaborator account.',
        valueType: 'boolean',
      },
      {
        value: 'billable',
        displayValue: 'Billable',
        displayInfoValue: 'Whether this activity is billable.',
        valueType: 'boolean',
      },
      {
        value: 'doc_id',
        displayValue: 'Document ID',
        displayInfoValue: 'Document ID.',
        valueType: 'string',
      },
      {
        value: 'doc_title',
        displayValue: 'Document Title',
        displayInfoValue: 'Document title.',
        valueType: 'string',
      },
      {
        value: 'doc_type',
        displayValue: 'Document Type',
        displayInfoValue:
          'Document type. Possible values: document, drawing, folder, form, html, jam, jpeg, mp4, mpeg, msexcel, mspowerpoint, msword, pdf, png, presentation, quicktime, script, shortcut, sites, spreadsheet, shared_drive, txt, unknown',
        valueType: 'string',
      },
      {
        value: 'is_encrypted',
        displayValue: 'Is Encrypted',
        displayInfoValue: 'Whether the file is client-side encrypted.',
        valueType: 'boolean',
      },
      {
        value: 'new_value',
        displayValue: 'New Value',
        displayInfoValue:
          'New Value. Possible values: can_comment, can_edit, can_respond, can_view, can_view_published, none, organizer, owner.',
        valueType: 'string',
      },
      {
        value: 'old_value',
        displayValue: 'Old Value',
        displayInfoValue:
          'Old Value. Possible values: can_comment, can_edit, can_respond, can_view, can_view_published, none, organizer, owner.',
        valueType: 'string',
      },
      {
        value: 'old_visibility',
        displayValue: 'Old Visibility',
        displayInfoValue: 'The old visibility of the document.',
        valueType: 'string',
      },
      {
        value: 'originating_app_id',
        displayValue: 'Originating App ID',
        displayInfoValue:
          'The Google Cloud Project ID of the application that performed the action.',
        valueType: 'string',
      },
      {
        value: 'owner',
        displayValue: 'Owner',
        displayInfoValue: `The email address of the document's owner. For items contained in a shared drive, the owner is the name of the shared drive.`,
        valueType: 'string',
      },
      {
        value: 'owner_is_shared_drive',
        displayValue: 'Owner is Shared Drive',
        displayInfoValue: `Boolean flag denoting whether the owner is a shared drive.`,
        valueType: 'boolean',
      },
      {
        value: 'owner_shared_drive_id',
        displayValue: 'Owner Shared Drive ID',
        displayInfoValue: `The shared drive root ID if the document owner is a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'primary_event',
        displayValue: 'Primary Event',
        displayInfoValue: `Whether this event is a primary event. A single user action in Drive can generate several events, multiple of which can be primary (primary_event=true).`,
        valueType: 'boolean',
      },
      {
        value: 'target_user',
        displayValue: 'Target User',
        displayInfoValue: 'The user that this event is about.',
        valueType: 'string',
      },
      {
        value: 'shared_drive_id',
        displayValue: 'Shared Drive ID',
        displayInfoValue: `The unique identifier of the shared drive. Only populated for events or items from a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'visibility',
        displayValue: 'Visibility',
        displayInfoValue: `Visibility of target file. Possible values: people_with_link, people_within_domain_with_link, private, public_in_the_domain, public_on_the_web, shared_externally, shared_internally, unknown`,
        valueType: 'string',
      },
      {
        value: 'visibility_change',
        displayValue: 'Visibility Change',
        displayInfoValue: `Change in overall visibility of the file. Possible values: internal, external, none`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'change_owner',
    displayValue: 'My Drive Ownership Transfer',
    displayInfoValue: 'Document ownership is transferred.',
    type: 'drive',
    lowestEntityAllowed: 'User',
    tags: ['New'],
    availableParameters: [
      {
        value: 'actor_is_collaborator_account',
        displayValue: 'Actor is Collaborator Account',
        displayInfoValue: 'Whether the actor is a collaborator account.',
        valueType: 'boolean',
      },
      {
        value: 'billable',
        displayValue: 'Billable',
        displayInfoValue: 'Whether this activity is billable.',
        valueType: 'boolean',
      },
      {
        value: 'doc_id',
        displayValue: 'Document ID',
        displayInfoValue: 'Document ID.',
        valueType: 'string',
      },
      {
        value: 'doc_title',
        displayValue: 'Document Title',
        displayInfoValue: 'Document title.',
        valueType: 'string',
      },
      {
        value: 'doc_type',
        displayValue: 'Document Type',
        displayInfoValue:
          'Document type. Possible values: document, drawing, folder, form, html, jam, jpeg, mp4, mpeg, msexcel, mspowerpoint, msword, pdf, png, presentation, quicktime, script, shortcut, sites, spreadsheet, shared_drive, txt, unknown',
        valueType: 'string',
      },
      {
        value: 'is_encrypted',
        displayValue: 'Is Encrypted',
        displayInfoValue: 'Whether the file is client-side encrypted.',
        valueType: 'boolean',
      },
      {
        value: 'new_owner',
        displayValue: 'New Owner',
        displayInfoValue: `The email address of the document's new owner. For items contained in a shared drive, the new owner is the name of the shared drive.`,
        valueType: 'string',
      },
      {
        value: 'new_owner_is_shared_drive',
        displayValue: 'New Owner is Shared Drive',
        displayInfoValue: `Boolean flag denoting whether the new owner is a shared drive.`,
        valueType: 'boolean',
      },
      {
        value: 'new_owner_shared_drive_id',
        displayValue: 'New Owner Shared Drive ID',
        displayInfoValue: `The shared drive root ID if the new document owner is a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'originating_app_id',
        displayValue: 'Originating App ID',
        displayInfoValue:
          'The Google Cloud Project ID of the application that performed the action.',
        valueType: 'string',
      },
      {
        value: 'owner',
        displayValue: 'Owner',
        displayInfoValue: `The email address of the document's owner. For items contained in a shared drive, the owner is the name of the shared drive.`,
        valueType: 'string',
      },
      {
        value: 'owner_is_shared_drive',
        displayValue: 'Owner is Shared Drive',
        displayInfoValue: `Boolean flag denoting whether the owner is a shared drive.`,
        valueType: 'boolean',
      },
      {
        value: 'owner_shared_drive_id',
        displayValue: 'Owner Shared Drive ID',
        displayInfoValue: `The shared drive root ID if the document owner is a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'primary_event',
        displayValue: 'Primary Event',
        displayInfoValue: `Whether this event is a primary event. A single user action in Drive can generate several events, multiple of which can be primary (primary_event=true).`,
        valueType: 'boolean',
      },
      {
        value: 'shared_drive_id',
        displayValue: 'Shared Drive ID',
        displayInfoValue: `The unique identifier of the shared drive. Only populated for events or items from a shared drive.`,
        valueType: 'string',
      },
      {
        value: 'visibility',
        displayValue: 'Visibility',
        displayInfoValue: `Visibility of target file. Possible values: people_with_link, people_within_domain_with_link, private, public_in_the_domain, public_on_the_web, shared_externally, shared_internally, unknown`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'CREATE_USER',
    displayValue: 'User Creation',
    displayInfoValue: 'A user is created.',
    type: 'admin',
    lowestEntityAllowed: 'Orgs',
    availableParameters: [
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `The new user's primary user email.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'ROLE_PERMISSION_CHANGE',
    displayValue: 'Role Permission Change',
    displayInfoValue: 'Any change in permissions for a given role.',
    type: 'admin',
    lowestEntityAllowed: 'Orgs',
    availableParameters: [
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `The new user's primary user email.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
      {
        value: 'ROLE_NAME',
        displayValue: 'Role Name',
        displayInfoValue: `The name of the role being modified.`,
        valueType: 'string',
      },
    ],
    tags: ['New'],
  },
  {
    value: 'ROLE_MEMBER_UPDATE',
    displayValue: 'Role Member Update',
    displayInfoValue: 'Any membership change for a given role.',
    type: 'admin',
    lowestEntityAllowed: 'Orgs',
    availableParameters: [
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `The user's primary user email.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
    tags: ['New'],
  },
  {
    value: 'DELETE_USER',
    displayValue: 'User Deletion',
    displayInfoValue: 'A user is deleted.',
    type: 'admin',
    lowestEntityAllowed: 'Domain',
    availableParameters: [
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `The new user's primary user email.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'CREATE_GROUP',
    displayValue: 'Group Creation',
    displayInfoValue: 'A group is created.',
    type: 'admin',
    lowestEntityAllowed: 'Domain',
    availableParameters: [
      {
        value: 'group_email',
        displayValue: 'Group Email',
        displayInfoValue: `Email address of the target group.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'CHANGE_PASSWORD',
    displayValue: 'User Password Change',
    displayInfoValue: 'A user changes their password.',
    type: 'admin',
    lowestEntityAllowed: 'User',
    availableParameters: [
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `The new user's primary user email.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'ROLE_SETTINGS_CHANGE',
    displayValue: 'Role Settings Change',
    displayInfoValue: 'Any role setting update for a given role.',
    type: 'admin',
    lowestEntityAllowed: 'Domain',
    availableParameters: [
      {
        value: 'ROLE_NAME',
        displayValue: 'Role Name',
        displayInfoValue: 'The name of the role whose settings were updated.',
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
    tags: ['New'],
  },
  {
    value: 'create_event',
    displayValue: 'Event Created',
    displayInfoValue: 'A calendar event is created.',
    type: 'calendar',
    lowestEntityAllowed: 'Resource Calendar',
    availableParameters: [],
  },
  {
    value: 'change_event',
    displayValue: 'Event Updated',
    displayInfoValue: 'A calendar event is updated.',
    type: 'calendar',
    lowestEntityAllowed: 'Resource Calendar',
    availableParameters: [],
  },
  // user update event
  {
    value: 'UPDATE',
    displayValue: 'User Update',
    displayInfoValue: 'User is updated.',
    type: 'admin',
    lowestEntityAllowed: 'Orgs',
    availableParameters: [
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `The new user's primary user email.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'ADD_GROUP_MEMBER',
    displayValue: 'Add Member',
    displayInfoValue: 'A member is added to a group.',
    type: 'admin',
    group: 'groupEvents',
    lowestEntityAllowed: 'Orgs',
    availableParameters: [
      {
        value: 'GROUP_EMAIL',
        displayValue: 'Group Email',
        displayInfoValue: `Email address of the target group.`,
        valueType: 'string',
      },
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `Primary email of the member added to the group.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'REMOVE_GROUP_MEMBER',
    displayValue: 'Delete Member',
    displayInfoValue: 'A member is deleted from a group.',
    type: 'admin',
    group: 'groupEvents',
    lowestEntityAllowed: 'Orgs',
    availableParameters: [
      {
        value: 'GROUP_EMAIL',
        displayValue: 'Group Email',
        displayInfoValue: `Email address of the target group.`,
        valueType: 'string',
      },
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `Primary email of the member removed from the group.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: 'UPDATE_GROUP_MEMBER',
    displayValue: 'Any Membership Changes',
    displayInfoValue: `Members are added and/or removed from a group.`,
    type: 'admin',
    group: 'groupEvents',
    lowestEntityAllowed: 'Orgs',
    availableParameters: [
      {
        value: 'GROUP_EMAIL',
        displayValue: 'Group Email',
        displayInfoValue: `Email address of the target group.`,
        valueType: 'string',
      },
      {
        value: 'USER_EMAIL',
        displayValue: 'User Email',
        displayInfoValue: `Primary email of the member whose membership changed.`,
        valueType: 'string',
      },
      {
        value: 'Actor',
        displayValue: 'Actor',
        displayInfoValue: `The actor's primary email.`,
        valueType: 'string',
      },
    ],
  },
  {
    value: '2sv_disable',
    displayValue: '2FA Turned Off',
    displayInfoValue: 'Two-step verification is disabled.',
    type: 'login',
    lowestEntityAllowed: 'User',
    tags: ['New'],
    availableParameters: [],
  },
  {
    value: 'change_calendar_acls',
    displayValue: 'Calendar Shared',
    displayInfoValue: 'A calendar ACL is changed.',
    type: 'calendar',
    group: 'personalCalendar',
    lowestEntityAllowed: 'User',
    availableParameters: [
      {
        value: 'access_level',
        displayValue: 'Access Level',
        displayInfoValue:
          'Access Level. Possible values: editor, freebusy, none, owner, read, root.',
        valueType: 'string',
      },
      {
        value: 'api_kind',
        displayValue: 'API Kind',
        displayInfoValue:
          'API Kind. Possible values: android, api_v3, caldav, ews, gdata, ical, ios, not_set, trip_service, web.',
        valueType: 'string',
      },
      {
        value: 'calendar_id',
        displayValue: 'Calendar ID',
        displayInfoValue: `Calendar Id of the relevant calendar in context of this action.`,
        valueType: 'string',
      },
      {
        value: 'grantee_email',
        displayValue: 'Grantee Email',
        displayInfoValue: 'Grantee Email.',
        valueType: 'string',
      },
      {
        value: 'user_agent',
        displayValue: 'User Agent',
        displayInfoValue:
          'The user agent from the request that triggered this action.',
        valueType: 'string',
      },
    ],
  },
  {
    value: 'login_failure',
    displayValue: 'Failed User Login',
    displayInfoValue: 'A user fails to log in.',
    type: 'login',
    lowestEntityAllowed: 'User',
    tags: ['New'],
    availableParameters: [
      {
        value: 'login_challenge_method',
        displayValue: 'Login Challenge Method',
        displayInfoValue:
          'Login challenge method. Possible values: access_to_preregistered_email, assistant_approval, backup_code, captcha, cname, cross_account, cross_device, deny, device_assertion, device_preregistered_phone, device_prompt, extended_botguard, google_authenticator, google_prompt, idv_any_email, idv_any_phone, idv_preregistered_email, idv_preregistered_phone, internal_two_factor, knowledge_account_creation_date, knowledge_cloud_pin, knowledge_date_of_birth, knowledge_domain_title, knowledge_employee_id, knowledge_historical_password, knowledge_last_login_date, knowledge_lockscreen, knowledge_preregistered_email, knowledge_preregistered_phone, knowledge_real_name, knowledge_secret_question, knowledge_user_count, knowledge_youtube, login_location, manual_recovery, math, none, offline_otp, oidc, other, outdated_app_warning, parent_auth, passkey, password, recaptcha, rescue_code, same_device_screenlock, saml, security_key, security_key_otp, time_delay, userless_fido, web_approval',
        valueType: 'string',
      },
      {
        value: 'login_failure_type',
        displayValue: 'Login Failure Type',
        displayInfoValue:
          '(Deprecated) The reason for the login failure. Possible values: login_failure_access_code_disallowed, login_failure_account_disabled, login_failure_invalid_password, login_failure_unknown',
        valueType: 'string',
      },
      {
        value: 'login_type',
        displayValue: 'Login Type',
        displayInfoValue:
          'The type of credentials used to attempt login. Possible values: exchange, google_password, reauth, saml, unknown',
        valueType: 'string',
      },
    ],
  },
  {
    value: 'login_success',
    displayValue: 'Successful User Login',
    displayInfoValue: 'A user successfully logs in.',
    type: 'login',
    lowestEntityAllowed: 'User',
    tags: ['New'],
    availableParameters: [
      {
        value: 'is_suspicious',
        displayValue: 'Is Suspicious',
        displayInfoValue:
          'The login attempt had some unusual characteristics, for example the user logged in from an unfamiliar IP address.',
        valueType: 'boolean',
      },
      {
        value: 'login_challenge_method',
        displayValue: 'Login Challenge Method',
        displayInfoValue:
          'Login challenge method. Possible values: access_to_preregistered_email, assistant_approval, backup_code, captcha, cname, cross_account, cross_device, deny, device_assertion, device_preregistered_phone, device_prompt, extended_botguard, google_authenticator, google_prompt, idv_any_email, idv_any_phone, idv_preregistered_email, idv_preregistered_phone, internal_two_factor, knowledge_account_creation_date, knowledge_cloud_pin, knowledge_date_of_birth, knowledge_domain_title, knowledge_employee_id, knowledge_historical_password, knowledge_last_login_date, knowledge_lockscreen, knowledge_preregistered_email, knowledge_preregistered_phone, knowledge_real_name, knowledge_secret_question, knowledge_user_count, knowledge_youtube, login_location, manual_recovery, math, none, offline_otp, oidc, other, outdated_app_warning, parent_auth, passkey, password, recaptcha, rescue_code, same_device_screenlock, saml, security_key, security_key_otp, time_delay, userless_fido, web_approval',
        valueType: 'string',
      },
      {
        value: 'login_type',
        displayValue: 'Login Type',
        displayInfoValue:
          'The type of credentials used to attempt login. Possible values: exchange, google_password, reauth, saml, unknown',
        valueType: 'string',
      },
    ],
  },
].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
export interface TriggerGroupUiOption {
  value: string;
  displayValue: string;
  appName: string;
  iconProps?: BoxedIconProps;
}

export const triggerGroupUiOptions: TriggerGroupUiOption[] = [
  {
    value: 'drive',
    displayValue: 'Drive Events',
    appName: 'drive',
    iconProps: { icon: AddToDrive, variant: 'amber' },
  },
  {
    value: 'admin',
    displayValue: 'Admin Events',
    appName: 'admin',
    iconProps: { icon: AdminPanelSettingsOutlined, variant: 'blue' },
  },
  {
    value: 'groupEvents',
    displayValue: 'Group Events',
    appName: 'admin',
    iconProps: { icon: GroupsOutlined, variant: 'green' },
  },
  {
    value: 'calendar',
    displayValue: 'Resource Calendar Events',
    appName: 'calendar',
    iconProps: { icon: CalendarTodayOutlined, variant: 'violet' },
  },
  {
    value: 'personalCalendar',
    displayValue: 'Personal Calendar Events',
    appName: 'calendar',
    iconProps: { icon: PermContactCalendarOutlined, variant: 'violet' },
  },
  {
    value: 'login',
    displayValue: 'Login Events',
    appName: 'login',
    iconProps: { icon: LockOutlined, variant: 'rose' },
  },
];

export const getEventUiGroup = (eventValue: string): string => {
  const option = triggerEventOptions.find((o) => o.value === eventValue);
  return option?.group ?? option?.type ?? 'drive';
};

export const generalFieldMergeTags = {
  title: 'General Fields',
  menu: [
    {
      value: 'RuleName',
      title: 'Rule Name',
    },
    {
      value: 'RuleDescription',
      title: 'Rule Description',
    },
    {
      value: 'TriggerGroup',
      title: 'Trigger Group',
    },
    {
      value: 'TriggerEvent',
      title: 'Trigger Event',
    },
    {
      value: 'Occurrence',
      title: 'Occurrences',
    },
  ],
};
